import { action, atom, readonly, withInit } from '@reatom/framework'
import BigNumber from 'bignumber.js'
import { match } from 'ts-pattern'

import { CurrencyModel } from '@root/entities/currency'
import { nativePriceAtom } from '@root/features/realtime/atoms'
import { tradePriceType } from '@root/features/trade/trading/atoms'
import { MathUtil, NumberUtil } from '@root/shared'
import { NumericFormat } from '@root/shared/core/format'
import { reatomNumericInput } from '@root/shared/lib'

import type { TypeHelper } from '@blade/std-lib'
import type { Atom } from '@reatom/framework'
import type { LimitOrder } from '@root/entities/limit-order'
import type { PairModel } from '@root/entities/pair'

type Args = {
  pairAtom: Atom<PairModel | undefined>
  buyAmountNativeAtom: Atom<BigNumber>
}

export const reatomLimitBuyInput = ({
  pairAtom,
  buyAmountNativeAtom,
}: Args) => {
  const targetTypeAtom = tradePriceType

  const targetPriceUsdAtom = reatomNumericInput({}, 'targetPriceUsdAtom')

  const referencePairPriceUsdAtom = atom<BigNumber | undefined>(
    undefined,
    'referencePairPriceUsdAtom',
  )

  targetPriceUsdAtom.raw.pipe(
    withInit((ctx) => {
      const initValue = (() => {
        const pair = ctx.get(pairAtom)
        if (!pair) {
          return undefined
        }
        return ctx.get(pair.price).priceUsd
      })()

      if (initValue) {
        referencePairPriceUsdAtom(ctx, initValue)
        return NumericFormat.formatPreset(initValue, 'full')
      }
      return ''
    }),
  )

  const targetMarketCapUsdAtom = reatomNumericInput(
    {},
    'targetMarketCapUsdAtom',
  )

  targetMarketCapUsdAtom.raw.pipe(
    withInit((ctx) => {
      const initValue = (() => {
        const pair = ctx.get(pairAtom)
        if (pair) {
          return ctx.get(pair.marketCapUsd)
        }
        return undefined
      })()

      if (initValue) {
        referenceMarketCapUsdAtom(ctx, initValue)
        return NumericFormat.formatPreset(initValue, 'full')
      }
      return ''
    }),
  )

  const referenceMarketCapUsdAtom = atom<BigNumber | undefined>(
    undefined,
    'referenceMarketCapUsdAtom',
  )

  const setTargetTypeAction = action(
    (ctx, targetType: LimitOrder.TargetType) => {
      targetTypeAtom(ctx, targetType)
      const pair = ctx.get(pairAtom)
      if (!pair) {
        return
      }
      // When switching target type, update reference values and reset inputs

      const priceUsd = ctx.get(pair.price).priceUsd
      const marketCapUsd = ctx.get(pair.marketCapUsd)

      referencePairPriceUsdAtom(ctx, priceUsd)
      referenceMarketCapUsdAtom(ctx, marketCapUsd)

      targetPriceUsdAtom.setRaw(
        ctx,
        NumericFormat.formatPreset(priceUsd, 'full'),
      )
      targetMarketCapUsdAtom.setRaw(
        ctx,
        NumericFormat.formatPreset(marketCapUsd, 'full'),
      )

      percentageAtom(ctx, 0)
    },
    'setTargetTypeAction',
  )

  const setTargetRawValueAction = action((ctx, rawValue: string) => {
    // Sync percentage with entered value
    const targetType = ctx.get(targetTypeAtom)
    if (targetType === 'absolute') {
      targetPriceUsdAtom.setRaw(ctx, rawValue)
      const referencePairPriceUsd = ctx.get(referencePairPriceUsdAtom)
      const priceUsd = NumberUtil.parseBigNumber(rawValue)
      if (referencePairPriceUsd && priceUsd) {
        const percentage = MathUtil.percentageChangeBn(
          referencePairPriceUsd,
          priceUsd,
        )
        percentageAtom(ctx, percentage.toNumber())
      }
    }
    if (targetType === 'market-cap') {
      targetMarketCapUsdAtom.setRaw(ctx, rawValue)
      const referenceMarketCapUsd = ctx.get(referenceMarketCapUsdAtom)
      const marketCapUsd = NumberUtil.parseBigNumber(rawValue)
      if (referenceMarketCapUsd && marketCapUsd) {
        const percentage = MathUtil.percentageChangeBn(
          referenceMarketCapUsd,
          marketCapUsd,
        )
        percentageAtom(ctx, percentage.toNumber())
      }
    }
  }, 'setTargetRawValueAction')

  const percentageAtom = atom(0, 'percentageAtom')

  const setPercentageAction = action((ctx, percents: number) => {
    // Sync value with entered percentage
    percentageAtom(ctx, percents)
    const targetType = ctx.get(targetTypeAtom)

    if (targetType === 'absolute') {
      const referencePriceUsd = ctx.get(referencePairPriceUsdAtom)
      if (!referencePriceUsd) {
        return
      }
      const nextTargetPriceUsd = referencePriceUsd.plus(
        referencePriceUsd.multipliedBy(percents / 100),
      )
      targetPriceUsdAtom.setRaw(
        ctx,
        NumericFormat.formatPreset(nextTargetPriceUsd, 'full'),
      )
      return
    }

    const referenceMarketCapUsd = ctx.get(referenceMarketCapUsdAtom)
    if (!referenceMarketCapUsd) {
      return
    }

    const nextTargetMarketCap = referenceMarketCapUsd.plus(
      referenceMarketCapUsd.multipliedBy(percents / 100),
    )
    targetMarketCapUsdAtom.setRaw(
      ctx,
      NumericFormat.formatPreset(nextTargetMarketCap, 'full'),
    )
  }, 'setPercentageAction')

  const receiveAtom = atom((ctx) => {
    const pair = ctx.spy(pairAtom)
    if (!pair) {
      return BigNumber(0)
    }

    const rateBy = ctx.spy(targetTypeAtom)

    const nativePrice = ctx.spy(nativePriceAtom)
    const amountNative = ctx.spy(buyAmountNativeAtom)

    const calculateReceiveAmount = (
      amountNative: BigNumber,
      nativeCurrencyPrice: BigNumber,
      targetPriceUsd: BigNumber,
    ) => {
      const amountUsd = amountNative.multipliedBy(nativeCurrencyPrice)
      const quoteAmount = amountUsd.dividedBy(targetPriceUsd)

      return NumberUtil.safeBigNumber(quoteAmount, 0)
    }

    if (rateBy === 'market-cap') {
      const targetMarketCap = ctx.spy(targetMarketCapUsdAtom)
      const targetPriceUsd = CurrencyModel.calculatePriceFromMarketCap(
        pair.quote,
        targetMarketCap,
      )

      return calculateReceiveAmount(amountNative, nativePrice, targetPriceUsd)
    }

    const targetPriceUsd = ctx.spy(targetPriceUsdAtom)

    return calculateReceiveAmount(amountNative, nativePrice, targetPriceUsd)
  }, 'receiveAtom')

  const outputSettingsAtom = atom(
    (
      ctx,
    ):
      | TypeHelper.OmitKey<
          LimitOrder.LimitOrderBuySettings,
          'expirationDate' | 'deadline'
        >
      | undefined => {
      const amountNative = ctx.get(buyAmountNativeAtom)
      const targetType = ctx.get(targetTypeAtom)
      const percentage = ctx.get(percentageAtom)

      const targetValue = match(targetType)
        .with('absolute', () => ctx.get(targetPriceUsdAtom))
        .with('market-cap', () => ctx.get(targetMarketCapUsdAtom))
        .exhaustive()

      const valueHigh = percentage >= 0 ? targetValue : undefined
      const valueLow = percentage < 0 ? targetValue : undefined

      return {
        purchaseAmountNative: amountNative,
        type: targetType,
        valueHigh: valueHigh,
        valueLow: valueLow,
      }
    },
    'outputSettingsAtom',
  )

  return {
    targetTypeAtom: targetTypeAtom.pipe(readonly),
    setTargetTypeAction,
    targetPriceUsdAtom,
    targetMarketCapUsdAtom,
    setTargetRawValueAction,
    percentageAtom: percentageAtom.pipe(readonly),
    setPercentageAction,
    receiveAtom,
    outputSettingsAtom,
  }
}
